import React from 'react';
import { Button } from '../ui/button';
import { Loader2, RefreshCw, OctagonX, AlertTriangle, CircleCheck, PackageX } from 'lucide-react';
import { fmt, money, when } from './aiqMeta';

const Fact = ({ label, children, testId }) => (
  <div className="min-w-0">
    <div className="text-[10px] uppercase tracking-wide text-zinc-500">{label}</div>
    <div className="text-sm text-zinc-900 truncate" data-testid={testId}>{children}</div>
  </div>
);

export const InvoiceSummaryStrip = ({ preview, onRefresh, refreshing }) => {
  const { customer, dispatch, totals } = preview;
  const aiqName = customer.aiq?.name;
  return (
    <div className="rounded-lg border bg-zinc-50/70 p-4 grid grid-cols-2 md:grid-cols-6 gap-4 items-end" data-testid="aiq-preview-summary">
      <div className="col-span-2">
        <Fact label="Invoice to" testId="aiq-preview-customer">
          <span className="font-medium">{customer.name}</span>
          <span className="ml-2 font-mono text-xs rounded bg-white border px-1.5 py-0.5">{customer.code || 'no code'}</span>
        </Fact>
        {aiqName && aiqName !== customer.name && <div className="text-xs text-zinc-500 mt-0.5">In AccountsIQ: {aiqName}</div>}
      </div>
      <Fact label="PO">{dispatch.po_number || '—'}</Fact>
      <Fact label="Dispatch date">{dispatch.dispatch_date || '—'}</Fact>
      <Fact label="Currency" testId="aiq-preview-currency">{totals?.currency || '—'}</Fact>
      <div className="flex flex-col gap-1 md:items-end">
        <div className="text-[10px] text-zinc-500">Prices synced <span className="font-mono">{when(preview.prices_synced_at)}</span></div>
        <Button size="sm" variant="outline" className="h-7 text-xs" onClick={onRefresh} disabled={refreshing || !preview.user_can_send} data-testid="aiq-preview-refresh-btn">
          {refreshing ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <RefreshCw className="w-3 h-3 mr-1" />}
          Refresh prices
        </Button>
      </div>
    </div>
  );
};

const NoticeBox = ({ items, tone, icon: Icon, title, testId }) => (
  <div className={`rounded-md border px-3 py-2.5 ${tone}`} data-testid={testId}>
    <div className="flex items-center gap-1.5 text-sm font-semibold"><Icon className="w-4 h-4" />{title}</div>
    <ul className="mt-1 ml-6 list-disc text-xs space-y-0.5">{items.map((t) => <li key={t}>{t}</li>)}</ul>
  </div>
);

export const InvoiceNotices = ({ preview }) => {
  const sent = preview.already_sent;
  const conn = preview.connection;
  return (
    <div className="space-y-2">
      {conn && conn.ok === false && !sent && (
        <NoticeBox items={[conn.summary, `Last checked ${when(conn.ran_at)}. Sending may fail until it's fixed.`]}
          tone="border-red-300 bg-red-50 text-red-800" icon={OctagonX} title="AccountsIQ connection problem" testId="aiq-preview-connection" />
      )}
      {sent && (
        <div className="rounded-md border border-emerald-200 bg-emerald-50 text-emerald-900 px-3 py-2.5 text-sm flex items-center gap-2" data-testid="aiq-preview-already-sent">
          <CircleCheck className="w-4 h-4 text-emerald-600" />
          Already in AccountsIQ as draft <span className="font-mono font-semibold">#{sent.invoice_id}</span>
          <span className="text-xs text-emerald-800/80">· sent by {sent.sent_by || '—'} {sent.sent_at ? `· ${when(sent.sent_at)}` : ''}</span>
        </div>
      )}
      {preview.blockers?.length > 0 && !sent && (
        <NoticeBox items={preview.blockers} tone="border-red-200 bg-red-50 text-red-800" icon={OctagonX}
          title="Fix before sending" testId="aiq-preview-blockers" />
      )}
      {preview.warnings?.length > 0 && (
        <NoticeBox items={preview.warnings} tone="border-amber-200 bg-amber-50 text-amber-900" icon={AlertTriangle}
          title="Check" testId="aiq-preview-warnings" />
      )}
    </div>
  );
};

export const NotSupplied = ({ items }) => (
  <div className="text-xs text-zinc-600 max-w-md" data-testid="aiq-preview-not-supplied">
    {items?.length > 0 && (
      <>
        <div className="flex items-center gap-1.5 font-semibold text-zinc-700 mb-1"><PackageX className="w-3.5 h-3.5" />Not supplied — not invoiced</div>
        <ul className="space-y-0.5">{items.map((n, i) => <li key={i}>{n.product_name} <span className="font-mono text-zinc-400">({fmt(n.units, 0)} units)</span></li>)}</ul>
      </>
    )}
  </div>
);

const TotalRow = ({ label, value, strong, testId }) => (
  <div className={`flex justify-between gap-8 ${strong ? 'border-t pt-1.5 mt-1.5 text-base font-semibold text-zinc-900' : 'text-sm text-zinc-600'}`}>
    <span>{label}</span><span className="font-mono" data-testid={testId}>{value}</span>
  </div>
);

export const InvoiceTotals = ({ totals }) => (
  <div className="rounded-lg border bg-white px-4 py-3 min-w-[260px] ml-auto" data-testid="aiq-preview-totals">
    <TotalRow label="Net" value={money(totals?.net, totals?.currency)} testId="aiq-preview-total-net" />
    <TotalRow label="Tax" value={money(totals?.tax, totals?.currency)} testId="aiq-preview-total-tax" />
    <TotalRow label="Invoice total" value={money(totals?.gross, totals?.currency)} strong testId="aiq-preview-total-gross" />
  </div>
);
